import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Chip,
  Grid,
  IconButton,
  Typography,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

/**
 * ChipsInput
 *
 * Props:
 *  - initialChips: string[] (opcional) chips iniciales
 *  - placeholder: string (opcional)
 *  - label: string (opcional)
 *  - onChange: function(chips: string[]) (opcional) callback cuando cambian los chips
 *  - maxChips: number (opcional) maxima cantidad de chips
 */
const ChipsInput = ({
  initialChips = [],
  placeholder = "Escribe un documento...",
  label = "Agregar documentos",
  onChange,
  maxChips,
  chipsValues,
}) => {
  const [value, setValue] = useState("");
  const [chips, setChips] = useState(() =>
    Array.isArray(chipsValues)
      ? chipsValues.map((c) => String(c).trim()).filter(Boolean)
      : []
  );
  const [error, setError] = useState("");

  const updateChips = (next) => {
    setChips(next);
    if (typeof onChange === "function") onChange(next);
  };

  const addChip = () => {
    const nextVal = String(value || "").trim();
    if (!nextVal) {
      setError("No puedes agregar un documento vacío.");
      return;
    }
    if (chips.includes(nextVal)) {
      setError("Ese documento ya está agregado.");
      return;
    }
    if (typeof maxChips === "number" && chips.length >= maxChips) {
      setError(`No puedes agregar más de ${maxChips} items.`);
      return;
    }

    updateChips([...chips, nextVal]);
    setValue("");
    setError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addChip();
    }
    if (e.key === "Escape") {
      setValue("");
      setError("");
    }
  };

  const removeChip = (chipToRemove) => {
    updateChips(chips.filter((c) => c !== chipToRemove));
  };

  useEffect(() => {
    if (Array.isArray(chipsValues)) {
      setChips(chipsValues.map((c) => String(c).trim()).filter(Boolean));
    }
  }, [chipsValues]);

  return (
    <Grid container>
      <Grid item xs={12} mb={"10px"}>
        <TextField
          fullWidth
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={handleKeyDown}
          helperText={
            error || `${chips.length}${maxChips ? ` / ${maxChips}` : ""}`
          }
          error={Boolean(error)}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={addChip} edge="end">
                  {<AddIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12}>
        {chips.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay documentos agregados.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {chips.map((c) => (
              <Chip
                key={c}
                label={c}
                onDelete={() => removeChip(c)}
                deleteIcon={<CloseIcon />}
                sx={{ marginBottom: 0.5 }}
              />
            ))}
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default ChipsInput;
